import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Bouton, Champ, Message } from '@journal/ui';
import { CoquilleAuth, LienAuth } from '../components/CoquilleAuth.tsx';
import { messageErreur, useSession, type DefiMfa } from '../lib/session.tsx';

/**
 * Écran de connexion, en deux temps si la double authentification est active.
 *
 * Le formulaire reste un vrai `<form>` avec un bouton `type="submit"` : c'est
 * ce qui fait fonctionner la validation du navigateur, la touche Entrée, et le
 * remplissage automatique des gestionnaires de mots de passe. Un `<div>` avec
 * un `onClick` casse les trois d'un coup.
 */
export function Connexion() {
  const { connecter, validerSecondFacteur, sessionExpiree } = useSession();
  const naviguer = useNavigate();
  const emplacement = useLocation();

  /*
   * Destination mémorisée par la redirection : après connexion, on y revient.
   * Elle est validée avant usage — une adresse absolue s'y glissant
   * transformerait l'écran de connexion en tremplin de redirection ouverte,
   * classique pour rendre crédible un lien d'hameçonnage.
   */
  const brut = (emplacement.state as { destination?: unknown } | null)?.destination;
  const destination =
    typeof brut === 'string' && brut.startsWith('/') && !brut.startsWith('//')
      ? brut
      : '/';

  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [code, setCode] = useState('');
  const [defi, setDefi] = useState<DefiMfa | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function soumettre(evenement: FormEvent) {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      if (defi) {
        await validerSecondFacteur(defi, code);
        naviguer(destination, { replace: true });
      } else {
        const demande = await connecter(email, motDePasse);
        if (demande) {
          setDefi(demande);
          // Le mot de passe ne sert plus : on le retire de la mémoire du
          // composant plutôt que de le laisser traîner dans l'état React.
          setMotDePasse('');
        } else {
          naviguer(destination, { replace: true });
        }
      }
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  }

  return (
    <CoquilleAuth
      titre={defi ? 'Vérification en deux étapes' : 'Connexion'}
      sousTitre={
        defi
          ? 'Une dernière étape avant d’accéder à votre espace.'
          : 'Accédez à votre cahier journal et à vos progressions.'
      }
      // Le pied disparaît pendant la vérification : proposer « créer un
      // espace » à quelqu'un dont la connexion est déjà à moitié faite est au
      // mieux hors sujet, au pire une invitation à tout recommencer.
      pied={
        defi ? undefined : (
          <>
            <p>
              <LienAuth vers="/mot-de-passe-oublie">Mot de passe oublié ?</LienAuth>
            </p>
            <p>
              <LienAuth vers="/inscription">Créer un compte</LienAuth>
            </p>
            {/* Les offres sont consultables sans compte : c'est la seule
                réponse à « qu'est-ce que je paierai ensuite ? » avant de
                s'inscrire. */}
            <p>
              <LienAuth vers="/offres">Voir les offres</LienAuth>
            </p>
          </>
        )
      }
    >
      <form onSubmit={soumettre} className="flex flex-col gap-4" noValidate>
        {sessionExpiree && !defi && (
          <Message ton="info">
            Votre session a expiré. Reconnectez-vous : vous reprendrez sur la
            page où vous étiez.
          </Message>
        )}
        {defi ? (
          <>
            <Message>
              Saisissez le code à six chiffres de votre application
              d'authentification, ou l'un de vos codes de récupération.
            </Message>
            <Champ
              libelle="Code de vérification"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              // `one-time-code` permet à iOS et Android de proposer le code
              // reçu directement au-dessus du clavier.
              autoComplete="one-time-code"
              inputMode="numeric"
              autoFocus
              required
            />
          </>
        ) : (
          <>
            <Champ
              libelle="Adresse électronique"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              autoFocus
              required
            />
            <Champ
              libelle="Mot de passe"
              type="password"
              value={motDePasse}
              onChange={(e) => setMotDePasse(e.target.value)}
              autoComplete="current-password"
              required
            />
          </>
        )}

        {erreur && <Message ton="erreur">{erreur}</Message>}

        {/* Pleine largeur : c'est l'unique action de l'écran, rien ne justifie
            de la réduire à la taille de son libellé. */}
        <Bouton
          type="submit"
          variante="primary"
          taille="lg"
          chargement={enCours}
          className="w-full"
        >
          {defi ? 'Vérifier' : 'Se connecter'}
        </Bouton>

        {defi && (
          <Bouton
            variante="ghost"
            onClick={() => { setDefi(null); setCode(''); setErreur(null); }}
            className="w-full"
          >
            Revenir en arrière
          </Bouton>
        )}
      </form>
    </CoquilleAuth>
  );
}
